import React, { useState, useRef } from "react";
import { TextField, Button, Typography, Box, Stack } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import app, { analytics, logEvent } from '../../services/firebase';
import { v4 as uuidv4 } from 'uuid';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, push, query, orderByChild, equalTo, onValue, off } from "firebase/database";
import ContactRegistryList from './ContactRegistryList'

// test 12/30/2024 - 11:13 PM:  um0ma22nGwfoYwcrLhLelbl7llT2
//                              um0ma22nGwfoYwcrLhLelbl7llT2
// copied from RTDB auth        um0ma22nGwfoYwcrLhLelbl7llT2
//
// (casey@gmail.com)            L5OLoYdBUlV3kfmmjjq6UobMOr82
// copied from RTDB             L5OLoYdBUlV3kfmmjjq6UobMOr82

function ContactForm() {
    const auth = getAuth(app);
    const dialNumberRef = useRef(null);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        projectId: '',
        firstname: '',
        lastname: '',
        email: (auth.currentUser ? auth.currentUser.email : '??'),
        phone: {
            countryCode: '+1', // Default country code
            areaCode: '',
            dialNumber: '',
            type: 'mobile', // Default phone type
        },
        projectTitle: '',
        projectRfqDescription: '',  // Or projectBrief
        projectReview: 'New',       // Default value
        // errors - here until all empty
    })

    const [errors, setErrors] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: {
            areaCode: '',
            dialNumber: '',
            type: 'mobile',
        },
        projectTitle: '',
        projectRfqDescription: '',
    });
    const [submitError, setSubmitError] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    const uuid = uuidv4; // Use the v4 method from the uuid library
    const database = getDatabase(app);
    const [loading, setLoading] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Profile DB
    const [profile, setProfile] = useState({
        firstname: "",
        lastname: "",
        userid: "",
        email: "",
    });


    // Yup schema for validation
    const validationSchema = yup.object().shape({
        firstname: yup.string().min(2, "Must have at least 2 characters").required("Required"),
        lastname: yup.string().min(2, "Must have at least 2 characters").required("Required"),        
        email: yup.string().email("Invalid email").required("Required"),
        phone: yup.object().shape({ // Nest phone fields within a phone object
            areaCode: yup.string().matches(/^\d{0,3}$/, "Must be 0-3 digits").nullable(),
            dialNumber: yup.string().matches(/^(\d{0,3}-?\d{0,4})?$/, "Must be XXX-XXXX format").nullable(),
        }),
        projectTitle: yup.string().required("Required"),
        projectRfqDescription: yup.string().required("Required"),
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Clear general submission error when user starts typing again
        if (submitError) setSubmitError(null);

        let error = null

        // Handle the nested phone object correctly
        if (name.startsWith('phone.')) {
            const phoneField = name.split('.')[1];  // Get field ('areaCode', 'dialNumber', etc.)
            setFormData(prevData => ({
                ...prevData,
                phone: {
                    ...prevData.phone,
                    [phoneField]: value,
                },
            }));

            if (name === 'phone.areaCode' && value.length === 3 && dialNumberRef.current.current) {
                dialNumberRef.current.focus();
            }

            if (name === 'phone.number && value.length === 3' && dialNumberRef.current.current) {
                setFormData(prevData => ({
                    ...prevData,
                    phone: {
                        ...prevData.phone,
                        [phoneField]: (value + '-'),
                    },
                }))
            }

        } else {
            setFormData(prevData => ({ ...prevData, [name]: value }));
        }

        // Validate using the *full path* for nested fields:
        validationSchema.validateAt(name, { ...formData, [name]: value }) // Use full path and spread formData!
        .then(() => {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        })
        .catch(err => {
            setErrors(prevErrors => ({ ...prevErrors, [name]: err.message }));
        });


        if (name === 'phone.areaCode' && value.length === 3 && dialNumberRef.current) {
        dialNumberRef.current.focus();
        }        
        
        if (error != null) { 
            console.log("Error is " + error)
        }

    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent default form submition
        setLoading(true)

        // try { // Yup exceptions
            try {
                // Validate entire form with Yup
                await validationSchema.validate(formData, { abortEarly: false });
                // No errors, proceed to LOG (not submit to Firebase for now)
                console.log("Form Data:", formData);

                const timestamp = new Date().toISOString();
                const newContactData = {
                    projectId : uuidv4(), // Generate a unique ID for the project
                    // uid: user.uid,
                    // userid: user.uid, // Or however you're getting the authenticated user's ID
                    dtAdded: Date.now(), // Milliseconds since epoch
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    email: formData.email,
                    phone: {
                        countryCode: "+1",
                        areaCode: formData.phone.areaCode,
                        dialNumber: formData.phone.dialNumber,
                        type: "mobile"
                    },
                    projectTitle: formData.projectTitle,
                    projectRfqDescription: formData.projectRfqDescription,
                    projectReview: "New",
                    timestamp,
                };

                const user = auth.currentUser;
                if (user) {
                    newContactData.uid = user.uid;
                }

                // Log the successful engagement event to Firebase Analytics
                logEvent(analytics, 'successful_engagement', {
                    engagement_type: 'project_submission',
                    project_id: newContactData.projectId
                });

                const contactRegistryRef = ref(database, 'contactRegistry');
                await push(contactRegistryRef, newContactData);

                // Handle success - navigate to confirmation page
                const newProjectId = newContactData.projectId;
                const newProjectTitle = newContactData.projectTitle;

                // Navigate to confirmation page with state
                navigate('/contactconfirmation', { 
                    state: { 
                        projectId: newProjectId,
                        projectTitle: newProjectTitle
                    } 
                });

            } catch (errSubmit) {
                if (errSubmit.inner) {
                    setErrors(prevErrors => ({ ...prevErrors, ...errSubmit.inner }));
                }
                setSubmitError(`Submission failed: ${errSubmit.message}`);
                console.error("Error submitting form:", errSubmit);
            } finally {
                setLoading(false);
                setHasSubmitted(false);
            }
    };


    return(
        <Box  
        component="form" 
        onSubmit={handleSubmit} 
        noValidate 
        sx={{ 
            mt: 1,
            mx: 'auto',
            width: '100%',
            maxWidth: {
                xs: '90%',
                sm: '80%',
                md: '75%',
                lg: '50%',
                xl: '40%',
            }
        }}>
        <div>Contact Us</div>
        <div>
            {loading ? (
                    <Box>
                        <CircularProgress />
                    </Box>
                ) : fetchError ? (
                    <Typography variant="body1" sx={{ color: 'red' }}>
                        Error: {fetchError}
                    </Typography>
                ) : ContactRegistryList && Array.isArray(ContactRegistryList) ? (
                    // Render the list 
                    ContactRegistryList.map((contactRegistry) => (
                        <Box key={contactRegistry.key} sx={{ mb: 2 }}> {
                            <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                            </Typography>
                        }
                        </Box>
                    ))
                ) : (
                    <Typography variant="caption">
                        No listed projects were found.
                    </Typography>
            )}
        </div>

        <Stack spacing={2}> {/* Use MUI Stack for layout */}

            <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                <Box component="sup" sx={{ fontSize: '0.7rem', verticalAlign: 'super' }}>*</Box> Required
            </Typography>

            {/* Display general submission error prominently */}
            {submitError && (
                <Typography color="error" variant="body1" sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
                    {submitError}
                </Typography>
            )}

            {/* Use TextFields linked to formData */}
            <TextField
                label="First Name"
                name="firstname" // Match formData keys
                value={formData.firstname}
                onChange={handleChange}
                error={Boolean(errors.firstname)}
                helperText={errors.firstname}
                required
                fullWidth
            />
            <TextField
                label="Last Name"
                name="lastname" // Match formData keys
                value={formData.lastname}
                onChange={handleChange}
                error={Boolean(errors.lastname)}
                helperText={errors.lastname}
                required
                fullWidth
            />
            {/* Similar TextFields for lastname, email, projectTitle, projectRfqDescription */}

            {/* Phone number fields */}
            <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                (Optional)
            </Typography>
            <Box sx={{ display: 'flex', gap: '1rem' }}> {/* Add a flexbox container */}
                <TextField
                    label="Area Code"
                    name="phone.areaCode"
                    type="tel"
                    inputMode="numeric"
                    value={formData.phone.areaCode}
                    onChange={handleChange}
                    error={Boolean(errors.phone?.areaCode)}
                    helperText={errors.phone?.areaCode}
                    placeholder="(123)"
                    sx={{ width: '30%' }} // Set width to 30%
                />
                <TextField
                    label="Phone Number"
                    name="phone.dialNumber"
                    type="tel"
                    inputMode="numeric"
                    inputRef={dialNumberRef}
                    value={formData.phone.dialNumber}
                    onChange={handleChange}
                    error={Boolean(errors.phone?.dialNumber)}
                    helperText={errors.phone?.dialNumber}
                    placeholder="123-4567"
                    sx={{ width: '70%' }}  // Set width to 70%
                />
            </Box>
            {/* email field */}
            <TextField
                label="Email"
                name="email" // Match formData keys
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                required
                fullWidth
            />
            {/* ... other form fields ... */}
            {/* project title field */}
            <Typography variant="body1" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                Briefly describe your project.  Give it a meaningful title as you might want to submit multiple projects.
            </Typography>
            <TextField
                label="Project Title"
                name="projectTitle" // Match formData keys
                value={formData.projectTitle}
                onChange={handleChange}
                error={Boolean(errors.projectTitle)}
                helperText={errors.projectTitle}
                required
                fullWidth
            />
            <TextField
                label="Project Description"
                name="projectRfqDescription" // Match formData keys
                value={formData.projectRfqDescription}
                onChange={handleChange}
                error={Boolean(errors.projectRfqDescription)}
                helperText={errors.projectRfqDescription}
                required
                fullWidth
                multiline
                rows={4}
            />

            <Button type="submit" variant="contained" onSubmit={handleSubmit}>Submit</Button>            
            {/* Cancel button if needed */}

            <Typography variant="body1" sx={{ fontSize: '0.8rem', wordWrap: 'break-word' }}>
                <Box component="sup" sx={{ fontSize: '0.7rem', verticalAlign: 'super' }}>
                    {auth.currentUser ? auth.currentUser.uid : 'Not logged in'}
                    <br />
                    {fetchError}
                    <br />
                    Client-SDK {auth.config.sdkClientVersion}
                </Box> 
            </Typography>

        </Stack>
    </Box>
    )
}

export default ContactForm;
